import { customFetch, getActiveFranchiseId } from '@agos/api-client'
import { tt } from '../i18n'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/**
 * Look and feel, ported from legacy `contexts/theme-context.tsx`.
 *
 * The server is the source of truth so a manager sees the same branding on the
 * till and at home; localStorage is the offline fallback that paints before the
 * fetch answers. The same stored shape feeds the customer site, so the
 * design-settings editor must keep producing it exactly (§14.12).
 */

const SERVER_SAVE_DEBOUNCE_MS = 500
const THEME_STORAGE_KEY = 'fastkitchen_theme'
const LOGO_STORAGE_KEY = 'fastkitchen_logo'
const LIGHT_LOGO_STORAGE_KEY = 'fastkitchen_logo_light'

export type ThemeMode = 'dark' | 'light' | 'auto'
/**
 * The **customer site's** light/dark, which is a different axis from `ThemeMode` - §9.303.
 *
 * `mode` above is the manager's own, its default is `dark`, and it drives a `dark` class. This drives
 * `data-scheme` on the customer site, its default is `light`, and it has no `auto`: the owner's reason
 * for not following the visitor's OS is that *"a franchise whose brand is black must look black on
 * every device."*
 */
export type ColorScheme = 'light' | 'dark'
export type ThemeFont = 'Inter' | 'Poppins' | 'Roboto' | 'Nunito'

export interface AccentColor {
  id: string
  name: string
  hsl: string
}

export const ACCENT_COLORS: AccentColor[] = [
  { id: 'red', name: tt('Červená'), hsl: '350 89% 55%' },
  { id: 'blue', name: tt('Modrá'), hsl: '217 91% 60%' },
  { id: 'green', name: tt('Zelená'), hsl: '142 71% 45%' },
  { id: 'violet', name: tt('Fialová'), hsl: '271 81% 60%' },
  { id: 'orange', name: tt('Oranžová'), hsl: '25 95% 53%' },
]

export const THEME_FONTS: ThemeFont[] = ['Inter', 'Poppins', 'Roboto', 'Nunito']

const FONT_STACKS: Record<ThemeFont, string> = {
  Inter: "'Inter', sans-serif",
  Poppins: "'Poppins', sans-serif",
  Roboto: "'Roboto', sans-serif",
  Nunito: "'Nunito', sans-serif",
}

export interface ThemeState {
  mode: ThemeMode
  /** The customer site's scheme, per franchise - §9.303. Never the manager's. */
  colorScheme: ColorScheme
  accentId: string
  font: ThemeFont
  restaurantName: string
  pageTitle: string
  pageDescription: string
  facebookUrl: string
  instagramUrl: string
  logoHeight: number
  homePageUrl: string
  mapEmbedUrl: string
  activeModules: string[]
}

export const DEFAULT_THEME: ThemeState = {
  mode: 'dark',
  // **`light`, where `mode` above defaults to `dark`** - §9.303, and the difference is the whole
  // point. FB-448 measured that wiring the customer site to `mode` would turn every franchise still
  // on the default into an unstyled dark public page. A franchise goes dark only by being set to.
  colorScheme: 'light',
  accentId: 'red',
  font: 'Inter',
  restaurantName: 'PizzaMajstro',
  pageTitle: '',
  pageDescription: '',
  facebookUrl: '',
  instagramUrl: '',
  logoHeight: 100,
  homePageUrl: '/uvodna-stranka',
  mapEmbedUrl: '',
  activeModules: [],
}

interface ThemeContextValue extends ThemeState {
  logoUrl: string | null
  lightLogoUrl: string | null
  setMode: (mode: ThemeMode) => void
  /** The customer site's scheme - §9.303. Distinct from `setMode`, which is the manager's. */
  setColorScheme: (colorScheme: ColorScheme) => void
  setAccent: (accentId: string) => void
  setFont: (font: ThemeFont) => void
  setLogoUrl: (url: string | null) => void
  setLightLogoUrl: (url: string | null) => void
  setRestaurantName: (name: string) => void
  setAdvanced: (partial: Partial<ThemeState>) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

/**
 * The cached design, if it was cached for **this** franchise — §9.174.
 *
 * One key served the whole origin, and two branches share one manager domain, so signing into Šurany
 * painted Nesvady's name until the server answered.
 *
 * **An envelope with no `franchiseId` is from before this change and is still read.** Refusing it
 * would flash the default theme at every existing user on their next load, and the server's answer
 * replaces it a moment later regardless. What it must not do is *survive* a franchise it does not
 * belong to, which is what the id is for.
 */
function readStoredTheme(fallback: ThemeState, franchiseId: string | null): ThemeState {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<ThemeState> & { franchiseId?: string | null }
    const cachedFor = parsed.franchiseId
    if (typeof cachedFor === 'string' && cachedFor !== (franchiseId ?? '')) return fallback
    const { franchiseId: _ignored, ...theme } = parsed
    return { ...fallback, ...theme }
  } catch {
    return fallback
  }
}

function readStoredString(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Repaints the document. The accent is three custom properties, not a class.
 *
 * **Exported for its test** - §9.303 put two independent light/dark axes in here, and the invariant
 * worth pinning is that they stay independent: `mode` drives a class for the manager, `colorScheme`
 * drives an attribute for the customer site, and the manager's default of `dark` must not reach the
 * customer's. That is a statement about the document, so the test makes one and reads it back.
 */
export function applyTheme(theme: ThemeState): void {
  const root = document.documentElement

  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const isDark = theme.mode === 'dark' || (theme.mode === 'auto' && prefersDark)
  root.classList.toggle('dark', isDark)

  // §9.303 - the customer site's second axis. An attribute rather than a class so it cannot collide
  // with the line above, and only ever set to `dark`: leaving it absent in the light scheme means the
  // light palette is simply `:root`, which is what every theme in `themes.css` is written against.
  if (theme.colorScheme === 'dark') root.setAttribute('data-scheme', 'dark')
  else root.removeAttribute('data-scheme')

  const accent = ACCENT_COLORS.find((color) => color.id === theme.accentId) ?? ACCENT_COLORS[0]
  if (accent) {
    root.style.setProperty('--primary', accent.hsl)
    root.style.setProperty('--ring', accent.hsl)
    root.style.setProperty('--sidebar-primary', accent.hsl)
  }

  root.style.setProperty('--font-sans', FONT_STACKS[theme.font])
  document.body.style.fontFamily = FONT_STACKS[theme.font]

  if (theme.pageTitle) document.title = theme.pageTitle
}

/** The subset persisted server-side, in the shape the customer site reads. */
const SERVER_FIELDS = [
  'mode',
  'colorScheme',
  'accentId',
  'font',
  'logoHeight',
  'homePageUrl',
  'activeModules',
  'restaurantName',
  'pageTitle',
  'pageDescription',
  'facebookUrl',
  'instagramUrl',
  'mapEmbedUrl',
] as const

function serverDesignOf(theme: ThemeState): Partial<ThemeState> {
  return Object.fromEntries(SERVER_FIELDS.map((field) => [field, theme[field]]))
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // §9.174 — which branch this browser is signed into. Read at every render rather than once, so a
  // sign-in that changes it re-runs the hydration below instead of keeping the previous branch's
  // design on screen.
  const franchiseId = getActiveFranchiseId()
  const [theme, setTheme] = useState<ThemeState>(() =>
    readStoredTheme(DEFAULT_THEME, franchiseId),
  )
  const [logoUrl, setLogoUrlState] = useState<string | null>(() => readStoredString(LOGO_STORAGE_KEY))
  const [lightLogoUrl, setLightLogoUrlState] = useState<string | null>(() =>
    readStoredString(LIGHT_LOGO_STORAGE_KEY),
  )

  // Nothing may be saved before the server has been read, or a tab opened with a
  // stale localStorage would overwrite the real design.
  const hydratedFromServer = useRef(false)
  // FB-069: and the render that *applies* what the server said must not save it
  // straight back. Flipping the flag above is not enough — it is set in the same
  // microtask as the `setTheme`, so it is already true by the time the echoing
  // render runs. This holds the payload that render is echoing.
  const echoOfServer = useRef<Partial<ThemeState> | null>(null)
  /** The payload the server is known to hold, so an identical one is not re-sent. */
  const savedPayload = useRef<string | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    // §9.174 — a franchise this provider has not read yet may not be written to. The flag is reset
    // here rather than left standing from the previous branch's read.
    hydratedFromServer.current = false
    savedPayload.current = null
    void (async () => {
      try {
        const response = await customFetch<Partial<ThemeState> | null>('/api/v1/settings/design')
        if (cancelled) return
        if (response && typeof response === 'object' && !('error' in response)) {
          echoOfServer.current = response
          setTheme((current) => ({ ...current, ...response }))
        }
        // **On the success path, not in a `finally`** — §9.174. This flag authorises the save
        // effect below, and setting it after a *failed* read let the previous branch's cached
        // design be PUT into the branch just signed into. The `catch` says "keep whatever
        // localStorage painted", and painting is all it may do.
        hydratedFromServer.current = true
      } catch {
        // Keep whatever localStorage painted, and save nothing.
      }
    })()
    return () => {
      cancelled = true
    }
    // Re-read when the branch changes: this provider wraps the login screen, so its one fetch used
    // to happen before anybody had chosen one.
  }, [franchiseId])

  useEffect(() => {
    applyTheme(theme)
    try {
      // §9.174 — stamped with the branch it belongs to, so the next branch on this domain does not
      // paint from it.
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ ...theme, franchiseId }))
    } catch {
      // painted anyway
    }

    if (!hydratedFromServer.current) return

    const payload = JSON.stringify(serverDesignOf(theme))
    if (echoOfServer.current) {
      // This render is the server's own answer coming back. Painting it is the
      // point; writing it again is how every page load became a PUT.
      echoOfServer.current = null
      savedPayload.current = payload
      return
    }
    // A colour picked and unpicked, or a field set back to what it was.
    if (savedPayload.current === payload) return

    // Coalesce the picker spam a colour wheel produces.
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      void customFetch<unknown>('/api/v1/settings/design', {
        method: 'PUT',
        body: payload,
      })
        .then(() => {
          savedPayload.current = payload
        })
        .catch(() => undefined)
    }, SERVER_SAVE_DEBOUNCE_MS)
  }, [theme, franchiseId])

  const makeLogoSetter = useCallback(
    (storageKey: string, setState: (url: string | null) => void) => (url: string | null) => {
      setState(url)
      try {
        if (url) localStorage.setItem(storageKey, url)
        else localStorage.removeItem(storageKey)
      } catch {
        // in-memory only
      }
    },
    [],
  )

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...theme,
      logoUrl,
      lightLogoUrl,
      setMode: (mode) => setTheme((current) => ({ ...current, mode })),
      setColorScheme: (colorScheme) => setTheme((current) => ({ ...current, colorScheme })),
      setAccent: (accentId) => setTheme((current) => ({ ...current, accentId })),
      setFont: (font) => setTheme((current) => ({ ...current, font })),
      setRestaurantName: (restaurantName) =>
        setTheme((current) => ({ ...current, restaurantName })),
      setAdvanced: (partial) => setTheme((current) => ({ ...current, ...partial })),
      setLogoUrl: makeLogoSetter(LOGO_STORAGE_KEY, setLogoUrlState),
      setLightLogoUrl: makeLogoSetter(LIGHT_LOGO_STORAGE_KEY, setLightLogoUrlState),
    }),
    [theme, logoUrl, lightLogoUrl, makeLogoSetter],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside ThemeProvider')
  return context
}
